// useTableSort.js - Reusable per-column sorting for react-data-table-component
//
// Mark a column as sortable by giving it a `sortField` (the DB column / row key):
//   { name: "Expense Type", selector: (row) => row.expenses_type_one, sortField: "expenses_type_one" }
//
// SERVER-SIDE (with useServerPagination) - backend receives `sortBy` + `sortDirection`:
//   const pagination = useServerPagination(url);
//   const sort = useServerSort(pagination, { sortBy: "createdAt", sortDirection: "DESC" });
//   <DataTable columns={sort.sortColumns(columns)} data={pagination.data} {...sort.sortProps} />
//   The header icon updates instantly; the request waits `delay` ms (default 400)
//   so rapid clicks send only one fetch.
//
// CLIENT-SIDE (with usePagination) - sorts the whole list BEFORE it is paginated:
//   const sort = useClientSort(rows);
//   const pagination = usePagination(sort.sortedData);
//   <DataTable columns={sort.sortColumns(columns)} data={pagination.paginatedData} {...sort.sortProps} />
import React, { useState, useMemo, useRef, useEffect } from "react";

const ASC = "ASC";
const DESC = "DESC";

// Header icon: stacked up/down chevrons. Both are dimmed when the column is not
// sorted; the active direction is highlighted when it is.
const SortIndicator = ({ direction }) => {
  const upOpacity = direction === ASC ? 1 : direction ? 0.25 : 0.6;
  const downOpacity = direction === DESC ? 1 : direction ? 0.25 : 0.6;

  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ms-2"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <polyline points="1.5,5 5,1.5 8.5,5" opacity={upOpacity} />
      <polyline points="1.5,9 5,12.5 8.5,9" opacity={downOpacity} />
    </svg>
  );
};

// Wraps each column that has a `sortField` with sortable flags + header icon
const decorateColumns = (columns, sortBy, sortDirection) =>
  columns.map((column) => {
    if (!column.sortField) return column;

    const isActive = column.sortField === sortBy;
    return {
      ...column,
      id: column.id ?? column.sortField,
      sortable: true,
      name: (
        <span className="d-inline-flex align-items-center">
          {column.name}
          <SortIndicator direction={isActive ? sortDirection : null} />
        </span>
      ),
    };
  });

// Props shared by both modes. sortServer stops the table from sorting only the
// visible page; the native icon is replaced by SortIndicator in the header.
const buildSortProps = (sortBy, sortDirection, onSort) => ({
  sortServer: true,
  onSort,
  sortIcon: <span />,
  defaultSortFieldId: sortBy || null,
  defaultSortAsc: sortDirection !== DESC,
});

export const useServerSort = (
  pagination,
  { sortBy = null, sortDirection = DESC, delay = 400 } = {},
) => {
  const [sort, setSort] = useState({ sortBy, sortDirection });
  const debounceTimer = useRef(null);

  // Drop a pending fetch if the table unmounts mid-delay
  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  const handleSort = (column, direction) => {
    const next = {
      sortBy: column.sortField,
      sortDirection: direction.toUpperCase(),
    };
    setSort(next);

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      // Merged into existing params (filters are kept) and resets to page 1
      pagination.updateParams(next);
    }, delay);
  };

  return {
    sortBy: sort.sortBy,
    sortDirection: sort.sortDirection,
    sortColumns: (columns) =>
      decorateColumns(columns, sort.sortBy, sort.sortDirection),
    sortProps: buildSortProps(sort.sortBy, sort.sortDirection, handleSort),
  };
};

// Reads "a.b.c" style paths from a row
const getByPath = (row, path) =>
  path.split(".").reduce((value, key) => value?.[key], row);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

const compareValues = (a, b) => {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1; // empties last

  if (typeof a === "boolean" || typeof b === "boolean") {
    return Number(a) - Number(b);
  }

  const aNum = Number(a);
  const bNum = Number(b);
  if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;

  if (
    (a instanceof Date || ISO_DATE.test(a)) &&
    (b instanceof Date || ISO_DATE.test(b))
  ) {
    return new Date(a) - new Date(b);
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const useClientSort = (
  data,
  { sortBy = null, sortDirection = DESC } = {},
) => {
  const [sort, setSort] = useState({ sortBy, sortDirection, column: null });

  const handleSort = (column, direction) => {
    setSort({
      sortBy: column.sortField,
      sortDirection: direction.toUpperCase(),
      column,
    });
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || !sort.sortBy) return data;

    const { column } = sort;
    const multiplier = sort.sortDirection === DESC ? -1 : 1;

    return [...data].sort((rowA, rowB) => {
      // Custom column comparator wins, same signature as react-data-table-component
      if (column?.sortFunction) {
        return column.sortFunction(rowA, rowB) * multiplier;
      }
      return (
        compareValues(
          getByPath(rowA, sort.sortBy),
          getByPath(rowB, sort.sortBy),
        ) * multiplier
      );
    });
  }, [data, sort]);

  return {
    sortedData,
    sortBy: sort.sortBy,
    sortDirection: sort.sortDirection,
    sortColumns: (columns) =>
      decorateColumns(columns, sort.sortBy, sort.sortDirection),
    sortProps: buildSortProps(sort.sortBy, sort.sortDirection, handleSort),
  };
};
